import React from "react";
import { motion, useReducedMotion } from "motion/react";

import { EASE_OUT } from "@/lib/ease";
import { ResourceGroupCard } from "./resource-details/ResourceDisclosure";
import { ListResource } from "./resource-details/ListResource";
import { LinkResource } from "./resource-details/LinkResource";
import { ChartResource } from "./resource-details/ChartResource";
import { TableResource } from "./resource-details/TableResource";
import { MapResource } from "./resource-details/MapResource";
import { MapAndImageResource } from "./resource-details/MapAndImageResource";
import { ReportResource } from "./resource-details/ReportResource";
import { ImageResource } from "./resource-details/ImageResource";
import { FloorplanResource } from "./resource-details/FloorplanResource";
import { FileResource } from "./resource-details/FileResource";
import JobResource from "./resource-details/JobResource";
import { LoD2Resource } from "./resource-details/LoD2Resource";
import { FollowUpSuggestionsResource } from "./resource-details/FollowUpSuggestionsResource";
import { ToolChoicesResource } from "./resource-details/ToolChoicesResource";
import { ApprovalRequestResource } from "./resource-details/ApprovalRequestResource";
import { ActionChipsResource } from "./resource-details/ActionChipsResource";
import { GuideNavigateBlock } from "./guide/GuideNavigateBlock";
import { GuideQuestionBlock } from "./guide/GuideQuestionBlock";
import { GuideConfirmBlock } from "./guide/GuideConfirmBlock";

interface ResourceDetailsProps {
    resources: any[];
    resourceSelected?: (resource: any) => void;
    // Optional 3rd arg carries routing hints (P3b: exclusiveToolId pins the resumed turn).
    onSubmitInput?: (input: string, prompt?: string, options?: { exclusiveToolId?: string; onAccepted?: () => void }) => void;
    isLastMessage?: boolean;
    pendingRequest?: boolean;
    /** Die Kacheln schweben ein — nur bei Antworten, die während der Sitzung entstehen. */
    animateIn?: boolean;
}

/**
 * Kachelarten, die eine aufklappbare Kopfzeile tragen und deshalb als Zeilen
 * **einer** Gruppe erscheinen (Zuschnitt des Bouncy-Akkordeons). Alles andere —
 * Dateien, Folgevorschläge, Werkzeugwahl, Aktions-Chips — sind Chips ohne
 * Kartenfläche und stehen für sich.
 */
const GROUPED_RESOURCE_TYPES = new Set([
    'list', 'link', 'chart', 'table', 'map', 'mapAndImage',
    'image', 'floorplan', 'lod2View', 'report',
]);

const ResourceDetailsComponent = ({ resources, resourceSelected, onSubmitInput, isLastMessage, pendingRequest, animateIn = false }: ResourceDetailsProps) => {
    const reduce = useReducedMotion();
    const filteredResources = resources.filter((resource) => resource.type !== 'source');
    
    // Deduplicate resources by reference or _id
    const uniqueResources = filteredResources.filter((resource, index, self) => {
        const key = resource.reference || resource._id || index;
        return index === self.findIndex(r => (r.reference || r._id || index) === key);
    });

    // Welche Zeile offen steht, meldet die Kachel selbst nach oben — die Gruppe
    // braucht es für die Kanten ihrer Nachbarinnen.
    const [openRows, setOpenRows] = React.useState<Record<number, boolean>>({});
    const reportOpen = React.useCallback((index: number, open: boolean) => {
        setOpenRows(previous => (previous[index] === open ? previous : { ...previous, [index]: open }));
    }, []);

    const isGrouped = (index: number) =>
        index >= 0
        && index < uniqueResources.length
        && GROUPED_RESOURCE_TYPES.has(uniqueResources[index]?.type);

    return <>
        {uniqueResources.map((resource, index) => {
            resource.options = resource.options || {};
            // Only convert to array for list-type resources that expect array data
            if (resource.type === 'list' && !Array.isArray(resource.data)) {
                resource.data = resource.data || [];
            }

            // Create a stable key that doesn't change during streaming
            // Use only the resource identity, not the index or title which might change
            const stableKey = resource._stableId || resource.reference || resource._id || `resource-${resource.type}-${index}`;
            // Unter der fertigen Antwort schwebt jede Kachel einzeln herein,
            // leicht versetzt. Ein Verlauf, der schon dastand, steht still:
            // `initial={false}` überspringt den Auftritt.
            const enterDelay = Math.min(index, 4) * 0.05;
            const grouped = isGrouped(index);

            const body = (() => {
                    // Safeguard against null resources - but be more specific about what's required for each type
                    if (!resource || !resource.type) {
                        return <></>;
                    }
                    
                    // Check for required properties based on resource type
                    if (resource.type === 'source') {
                        // Source resources need provider and tool/url
                        if (!resource.provider || (!resource.tool && !resource.url)) {
                            return <></>;
                        }
                    } else if (resource.type === 'map') {
                        // Map resources need either data or reference
                        if (!resource.data && !resource.reference) {
                            return <></>;
                        }
                    } else {
                        // Many types are persisted with url only (e.g. image, file); streaming may add synthetic reference.
                        const hasRenderablePayload =
                            // A background job card carries only its job id; it loads the rest itself.
                            (resource.type === 'job' && resource.jobId) ||
                            resource.data ||
                            resource.reference ||
                            resource.content ||
                            resource.url ||
                            (typeof resource.imageData === 'string' && resource.imageData.length > 0);
                        if (!hasRenderablePayload) {
                            return <></>;
                        }
                    }

                    switch (resource.type) {
                        case 'list': 
                            return <ListResource key={stableKey} resource={resource} stableKey={stableKey}></ListResource>
                        case 'link': 
                            return <LinkResource key={stableKey} resource={resource} stableKey={stableKey}></LinkResource>
                        case 'image': 
                            return <ImageResource key={stableKey} resource={resource} stableKey={stableKey}></ImageResource>
                        case 'chart': 
                            return <ChartResource key={stableKey} resource={resource} stableKey={stableKey} resourceSelected={resourceSelected}></ChartResource>
                        case 'table': 
                            return <TableResource key={stableKey} resource={resource} stableKey={stableKey}></TableResource>
                        case 'map': 
                            return <MapResource key={stableKey} resource={resource} stableKey={stableKey} resourceSelected={resourceSelected}></MapResource>
                        case 'mapAndImage': 
                            return <MapAndImageResource key={stableKey} resource={resource} stableKey={stableKey} resourceSelected={resourceSelected}></MapAndImageResource>
                        case 'lod2View': 
                            return <LoD2Resource key={stableKey} resource={resource} stableKey={stableKey}></LoD2Resource>
                        case 'report': 
                            return <ReportResource key={stableKey} resource={resource} stableKey={stableKey}></ReportResource>
                        case 'floorplan': 
                            return <FloorplanResource key={stableKey} resource={resource} stableKey={stableKey}></FloorplanResource>
                        case 'file': 
                            return <FileResource key={stableKey} resource={resource} stableKey={stableKey} resourceSelected={resourceSelected}></FileResource>
                        case 'job':
                            return <JobResource key={stableKey} resource={resource} stableKey={stableKey}></JobResource>
                        case 'followUpSuggestions':
                            return <FollowUpSuggestionsResource key={stableKey} resource={resource} stableKey={stableKey} onSubmitInput={onSubmitInput} isLastMessage={isLastMessage} pendingRequest={pendingRequest}></FollowUpSuggestionsResource>
                        case 'toolChoices':
                            return <ToolChoicesResource key={stableKey} resource={resource} stableKey={stableKey} onSubmitInput={onSubmitInput} isLastMessage={isLastMessage} pendingRequest={pendingRequest}></ToolChoicesResource>
                        case 'approvalRequest':
                            return <ApprovalRequestResource key={stableKey} resource={resource} stableKey={stableKey} onSubmitInput={onSubmitInput} isLastMessage={isLastMessage} pendingRequest={pendingRequest}></ApprovalRequestResource>
                        case 'actionChips':
                            return <ActionChipsResource key={stableKey} resource={resource} stableKey={stableKey} onSubmitInput={onSubmitInput} isLastMessage={isLastMessage} pendingRequest={pendingRequest}></ActionChipsResource>
                        // Portal guide: jump button (stage 2), follow-up question and approval
                        // card (stage 3). While streaming they sit inline (placeholder in the
                        // text, `ChatMessage`); the persisted history carries no placeholder,
                        // so they land here — without these cases the block would flash briefly
                        // and vanish as soon as the persisted message replaced the stream.
                        case 'navigate':
                            return <GuideNavigateBlock key={stableKey} resource={resource} />
                        case 'question':
                            return <GuideQuestionBlock key={stableKey} resource={resource} collapsed={isLastMessage === false} />
                        case 'confirm':
                            return <GuideConfirmBlock key={stableKey} resource={resource} collapsed={isLastMessage === false} />
                        default: 
                            return <></>;
                    }
            })();

            // Aufklappbare Kacheln sind Zeilen einer Gruppe: bündig, solange sie
            // zu sind, herausgelöst, sobald eine aufgeht. Nachbarschaft zählt
            // nur zwischen zwei Gruppenzeilen — ein Chip dazwischen trennt.
            return grouped
                ? <ResourceGroupCard
                    key={stableKey}
                    open={!!openRows[index]}
                    previousOpen={isGrouped(index - 1) && !!openRows[index - 1]}
                    nextOpen={isGrouped(index + 1) && !!openRows[index + 1]}
                    isFirst={!isGrouped(index - 1)}
                    isLast={!isGrouped(index + 1)}
                    onOpenChange={(open) => reportOpen(index, open)}
                    animateIn={animateIn}
                    enterDelay={enterDelay}
                >
                    {body}
                </ResourceGroupCard>
                : <motion.div
                    key={stableKey}
                    style={{ width: '100%' }}
                    initial={animateIn && !reduce ? { opacity: 0, y: 12 } : false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.32, ease: EASE_OUT, delay: enterDelay }}
                >
                    {body}
                </motion.div>;
        })}
    </>;
};

// Temporarily disable memo to test if it's causing the chart error
export const ResourceDetails = ResourceDetailsComponent;
